export const arkRegex = /(3:[1-2]:[^?/]*)/g

/**
 * Extracts the ark from a URL and normalizes it to uppercase.
 * @param {string} url the URL to extract the ark from
 * e.g. http://localhost:5001/ark:/61903/3:1:3QSQ-G9MT-86K2?wc=QZXP-5CT%3A790105301%2C796133301%2C796133302%2C952084901%3Fcc%3D2000219&cc=2000219 -> 3:1:3QSQ-G9MT-86K2
 * @returns the ark in uppercase
 */
export function extractArk(url) {
  const match = url?.match(arkRegex)
  return match?.[0]?.toUpperCase() ?? null
}

/**
 * Normalizes the ARK in a URL to uppercase.
 * @param {string} url the URL to normalize
 * e.g. http://localhost:5001/ark:/61903/3:1:3qs7-89MX-QBHD?lang=es -> http://localhost:5001/ark:/61903/3:1:3QS7-89MX-QBHD?lang=es
 * @returns the URL with the ARK normalized to uppercase
 */
export function normalizeArkInUrl(url) {
  if (!url) return url
  const ark = extractArk(url)
  if (!ark) return url
  return url.replaceAll(arkRegex, ark)
}

/**
 * Helper to extract collectionContext from query parameters. This finds the collectionContext in the various ways it can be in the URL:
 * - as the cc param ex. https://www.familysearch.org/ark:/61903/3:1:S3HT-68PT-RH?wc=Q678-76V%3A179532601%26cc%3D1908531&cc=1908531&lang=en&i=0
 * - embedded in the wc param ex.https://www.familysearch.org/ark:/61903/3:1:S3HY-61MP-DP?wc=Q678-766%3A179532901%26cc%3D1908531&lang=en&i=4
 * @returns {string} collectionId
 */
export function getCollectionContextFromUrl() {
  const searchParams = new URLSearchParams(window.location.search)
  let context = searchParams.get('cc')
  // If not found, try to extract cc from wc param--sometimes it is embedded in there
  if (!context) {
    const wcParam = searchParams.get('wc')
    if (wcParam) {
      // decode wc param and parse for cc
      const decodedWc = decodeURIComponent(wcParam)
      const wcParams = new URLSearchParams(decodedWc.replace(/^[^?]*[?&]/, ''))
      context = wcParams.get('cc')
    }
  }
  return context
}

/**
 * Get the collection id from the waypoint parameter.
 * @param {string} owcParameter
 * @param {string} waypointURL
 * @returns {string} collectionId
 */
export default function getCollectionId(owcParameter, waypointURL = null) {
  if (owcParameter?.startsWith('http')) {
    // e.g. https://www.familysearch.org/service/cds/recapi/collections/1784216/waypoints -> 1784216
    const collectionIdMatchArray = owcParameter.match(/(?<=\/)[\d]+(?=\/waypoints)/)
    const collectionId = collectionIdMatchArray?.[0]

    return collectionId
  }

  if (waypointURL) {
    return (waypointURL.match(/cc=([^&]*)/) || [])?.[1]
  }
  return owcParameter
}

/**
 * Get the access information contained in the image rights for GedcomX image.
 * @param {Object} imageData The image data object
 * @returns {Object} A rights object with all of properties listed:
 *    restricted {Bool}
 *    allowDownload {Bool}
 *    allowPrint {Bool}
 *    authorized {Bool}
 */
export function getImageRights(imageData) {
  const access = {}
  const rightsString = imageData?.meta?.sourceDescriptions?.[0]?.rights?.[0]
  let rights = null

  if (rightsString) {
    rights = new URLSearchParams(rightsString.split('?')[1])
    // Convert 'true' or 'false' to boolean
    rights.forEach((value, key) => {
      if (value === 'true') {
        access[key] = true
      } else if (value === 'false') {
        access[key] = false
      } else {
        access[key] = decodeURIComponent(value)
      }
    })
    return access
  }

  return access
}

/**
 * Returns a collection-id value suitable for the `groupId` query param consumed by
 * @fs/zion-image-viewer's attribution modal. Returns null when no collection ID can be derived;
 * callers should not set `groupId` in that case so zion falls back to per-image keying.
 *
 * `state.collectionContext` is populated from `imageData.collections[0]` or the URL's `cc`/`wc`
 * params (see filmDataReducer SET_IMAGE_DATA), so it covers the common case. Falls back to parsing
 * `meta.description` (`sd_c_{olibId}`) for the edge case where the response has an empty
 * `collections` array but the backend still derived a parent collection (observed in some
 * imageData fixtures where collections=[] but meta.description=sd_c_<id>).
 *
 * @param {object} state Film viewer state
 * @returns {string|null}
 */
const collectionDescriptionPattern = /^sd_c_(\d+)$/
export function getCollectionIdForGroupId(state) {
  if (state?.collectionContext) return state.collectionContext
  const metaDesc = state?.imageData?.meta?.description
  const match = typeof metaDesc === 'string' ? collectionDescriptionPattern.exec(metaDesc) : null
  if (match) return match[1]
  return null
}

/**
 * Small helper function to clip index to max number of images
 * @param {Number} index - current index to be normalized to the length of the images array
 * @param {Number} maxLength -length of the images array (-1 is handled inside the function to make function calls cleaner)
 * @returns {Number}
 */
export function normalizeIndex(index, maxLength) {
  return maxLength ? Math.min(index, maxLength - 1) : index
}

/**
 * Extracts the title and url from imageData.
 * @param {Object} state - The state object.
 * @returns {Object} title and, if available, url
 */
export function getArtifactTitleAndUrl(imageData) {
  const title = imageData?.meta?.sourceDescriptions?.[0]?.titles?.[0]?.value?.split('; ')?.[0] ?? null
  const url = imageData?.meta?.sourceDescriptions?.[0]?.about
  if (!url) return { title, url: null }
  return { title, url }
}

/**
 * Determines if the image is in SLS by checking the SystemOfRecord field of its indexed records.
 * SLS images are editable in the shared image viewer (R2). This is the same check the record details
 * page uses when deciding to link "View Original Document" to the shared image viewer (see checkIsSLS
 * in @fs/zion-record-details RecordProvider).
 * @param {Object} imageData The response from the call to filmdatainfo with type=image-data
 * @returns {boolean} true if any record on the image has SystemOfRecord=SLS
 */
export function imageIsInSls(imageData) {
  return Boolean(
    imageData?.records?.some(
      (record) =>
        record?.fields?.find((field) => field?.type === 'http://familysearch.org/types/fields/SystemOfRecord')
          ?.values?.[0]?.text === 'SLS'
    )
  )
}
